import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Input,
  InputNumber,
  Popconfirm,
  Segmented,
  Select,
  Switch,
  Tag,
  message,
} from "antd";
import {
  CodeOutlined,
  AudioOutlined,
  CopyOutlined,
  DeleteOutlined,
  LinkOutlined,
  MobileOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { channelsForResponseMode, EMBED_DEVICE_TARGET, EMBED_LAUNCHER_POSITION, EMBED_TRIGGER_PRESET, EMBED_TRIGGER_TYPES, PHONE_CALLS_LOCKED, SURVEY_RESPONSE_MODE } from "./constants";
import { CALL_LANGUAGES, PERSONA_OPTIONS } from "pages/Sonic/constants";
import { languageForUi } from "pages/Sonic/languageUtils";
import PhoneVoiceTest from "./PhoneVoiceTest";
import PhoneTriggerPanel from "./PhoneTriggerPanel";
import LinkSlugEditor from "./LinkSlugEditor";
import KioskModeToggle from "./KioskModeToggle";
import SurveyKnowledgePanel from "pages/FeedbackCapture/settings/SurveyKnowledgePanel";
import styles from "./style.module.scss";

const CHANNEL_ICONS = {
  link: <LinkOutlined />,
  code: <CodeOutlined />,
  bolt: <ThunderboltOutlined />,
  mobile: <MobileOutlined />,
  phone: <PhoneOutlined />,
  mic: <AudioOutlined />,
};

const SLUG_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";
const randomSlug = () =>
  Array.from(
    { length: 6 },
    () => SLUG_ALPHABET[Math.floor(Math.random() * SLUG_ALPHABET.length)]
  ).join("");

const copyToClipboard = (text, okMsg) => {
  try {
    navigator.clipboard.writeText(text);
    message.success(okMsg);
  } catch (_) {
    message.info(text);
  }
};

// The "Distribute" step of the survey builder. Purely controlled: it never owns
// state, every edit bubbles up through onChange(nextDistribution) so the drawer
// keeps a single source of truth it can persist on save.
const DistributeStep = ({
  distribution,
  onChange,
  instrumentId,
  surveyTitle,
  questions,
  onLinkRegenerated,
  capabilities,
  onUpgrade,
  responseMode = SURVEY_RESPONSE_MODE.TEXT,
  surveyLanguage,
}) => {
  const { t, i18n } = useTranslation();
  const dist = distribution;
  // Without a chosen call language the call speaks the survey's own language, as the API does.
  const callLanguage = dist.phone_call?.language || languageForUi(surveyLanguage || i18n.language);
  const canTriggered = !!capabilities?.canTriggeredSend;
  // AI phone surveys come with Sonic Survey Pro and Pro+, like AI voice capture.
  const canPhone = !!capabilities?.canVoice;
  const canAdvancedTriggers = !!capabilities?.canAdvancedTriggers;
  const allowedSources = capabilities?.allowedTriggerSources || [];

  const setChannel = (key, patch) =>
    onChange({ ...dist, [key]: { ...dist[key], ...patch } });

  // The company's default disclosure, shown where this survey leaves its own empty.
  const [companyDisclosure, setCompanyDisclosure] = useState("");
  const onKnowledgeLoaded = useCallback((data) => setCompanyDisclosure(data?.disclosure || ""), []);

  const copyLink = () =>
    copyToClipboard(
      dist.link_qr.fullUrl || "",
      t("surveyManagement.distribute.link.copied")
    );

  const regenerate = async () => {
    if (instrumentId) {
      try {
        const { regenerateLinkQr } = await import("./surveyService");
        const channel = await regenerateLinkQr(instrumentId);
        const slug = channel.slug || channel.config?.slug || "";
        const fullUrl = channel.config?.full_url || `${window.location.origin}/s/${slug}`;
        setChannel("link_qr", { enabled: true, slug, fullUrl });
        if (onLinkRegenerated) onLinkRegenerated({ slug, fullUrl });
        message.success(t("surveyManagement.distribute.link.regenerated"));
      } catch (_) {
        message.error(t("surveyManagement.msg_save_error"));
      }
      return;
    }
    const slug = `pps-${randomSlug()}`;
    setChannel("link_qr", {
      enabled: true,
      slug,
      fullUrl: `${window.location.origin}/s/${slug}`,
    });
    message.success(t("surveyManagement.distribute.link.regenerated"));
  };

  const toggleChannel = async (key, enabled) => {
    if (key === "phone_call" && enabled && !canPhone) {
      message.info(t("surveyManagement.distribute.phone.locked_toast"));
      if (onUpgrade) onUpgrade("pro");
      return;
    }
    if (key === "triggered_send" && enabled && !canTriggered) {
      message.info(t("surveyManagement.distribute.triggered.locked_toast"));
      if (onUpgrade) onUpgrade("pro");
      return;
    }
    if (key === "link_qr" && enabled && !dist.link_qr?.slug) {
      if (instrumentId) {
        await regenerate();
        return;
      }
      setChannel(key, { enabled });
      return;
    }
    setChannel(key, { enabled });
  };

  const embedSnippet =
    `<script src="https://cdn.pivony.com/embed.js"\n` +
    `  data-survey="${dist.link_qr.slug || "survey"}"\n` +
    `  data-mode="${dist.web_embed.displayMode}"></script>`;

  const copySnippet = () =>
    copyToClipboard(embedSnippet, t("surveyManagement.distribute.embed.copied"));

  const updateRule = (idx, patch) =>
    setChannel("web_embed", {
      triggerRules: dist.web_embed.triggerRules.map((r, i) =>
        i === idx ? { ...r, ...patch } : r
      ),
    });

  const addRule = () =>
    setChannel("web_embed", {
      triggerRules: [
        ...dist.web_embed.triggerRules,
        { rule_type: "time_on_page", value: 10 },
      ],
    });

  const removeRule = (idx) =>
    setChannel("web_embed", {
      triggerRules: dist.web_embed.triggerRules.filter((_, i) => i !== idx),
    });

  const isPageLoadPreset =
    dist.web_embed.triggerPreset === EMBED_TRIGGER_PRESET.PAGE_LOAD;
  const isLauncherPreset =
    dist.web_embed.triggerPreset === EMBED_TRIGGER_PRESET.LAUNCHER_CLICK;
  const showCustomTriggerRules =
    !isPageLoadPreset && !isLauncherPreset;

  const setTriggerPreset = (preset) => {
    if (preset === EMBED_TRIGGER_PRESET.PAGE_LOAD) {
      setChannel("web_embed", {
        triggerPreset: EMBED_TRIGGER_PRESET.PAGE_LOAD,
        triggerRules: [],
      });
      return;
    }
    if (preset === EMBED_TRIGGER_PRESET.LAUNCHER_CLICK) {
      setChannel("web_embed", {
        triggerPreset: EMBED_TRIGGER_PRESET.LAUNCHER_CLICK,
        triggerRules: [],
      });
      return;
    }
    setChannel("web_embed", {
      triggerPreset: EMBED_TRIGGER_PRESET.CUSTOM,
      triggerRules: dist.web_embed.triggerRules?.length
        ? dist.web_embed.triggerRules
        : [{ rule_type: "time_on_page", value: 15 }],
    });
  };

  const renderRuleValue = (rule, idx) => {
    if (rule.rule_type === "exit_intent") {
      return (
        <span className={styles.distRuleStatic}>
          {t("surveyManagement.distribute.embed.exit_intent_desc")}
        </span>
      );
    }
    if (rule.rule_type === "url_match") {
      return (
        <Input
          size="small"
          value={rule.value || ""}
          placeholder="/checkout/*"
          onChange={(e) => updateRule(idx, { value: e.target.value })}
        />
      );
    }
    const suffix =
      rule.rule_type === "scroll_depth"
        ? t("surveyManagement.distribute.embed.percent_of_page")
        : t("surveyManagement.distribute.embed.seconds");
    return (
      <span className={styles.distRuleValueRow}>
        <InputNumber
          size="small"
          min={0}
          value={rule.value}
          onChange={(v) => updateRule(idx, { value: v })}
        />
        <span className={styles.distRuleSuffix}>{suffix}</span>
      </span>
    );
  };

  return (
    <div>
      <p className={styles.distIntro}>
        {t("surveyManagement.distribute.subheading")}
      </p>

      <div className={styles.distCardGrid}>
        {channelsForResponseMode(responseMode).map((ch) => {
          const chState = dist[ch.key] || {};
          const active = !!chState.enabled;
          const lockedTrigger =
            (ch.key === "triggered_send" && !canTriggered && !ch.comingSoon) ||
            (ch.key === "phone_call" && !canPhone);
          return (
            <div
              key={ch.key}
              className={`${styles.distCard} ${
                active ? styles.distCardActive : ""
              } ${ch.comingSoon || lockedTrigger ? styles.distCardDisabled : ""}`}
            >
              <div className={styles.distCardTop}>
                <span className={styles.distCardIcon}>
                  {CHANNEL_ICONS[ch.icon]}
                </span>
                <Switch
                  size="small"
                  checked={active}
                  disabled={ch.comingSoon || lockedTrigger}
                  onChange={(v) => toggleChannel(ch.key, v)}
                />
              </div>
              <div className={styles.distCardTitle}>
                {t(`surveyManagement.distribute.channel.${ch.key}.title`)}
                {ch.comingSoon ? (
                  <Tag className={styles.distSoonTag}>
                    {t("surveyManagement.distribute.soon")}
                  </Tag>
                ) : null}
                {lockedTrigger ? (
                  <Tag className={styles.distSoonTag}>
                    {t("surveyManagement.survey_pro_badge", "Pro")}
                  </Tag>
                ) : null}
              </div>
              <div className={styles.distCardDesc}>
                {lockedTrigger
                  ? t(
                      ch.key === "phone_call"
                        ? "surveyManagement.distribute.phone.locked_desc"
                        : "surveyManagement.distribute.triggered.locked_desc"
                    )
                  : t(`surveyManagement.distribute.channel.${ch.key}.desc`)}
              </div>
              {lockedTrigger && onUpgrade ? (
                <Button
                  type="link"
                  size="small"
                  onClick={() => onUpgrade("pro")}
                >
                  {t("surveyManagement.upgrade_plan_cta")}
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>

      {dist.link_qr.enabled ? (
        <Card
          size="small"
          className={styles.distPanel}
          title={
            <span>
              <LinkOutlined />{" "}
              {t("surveyManagement.distribute.link.panel_title")}
            </span>
          }
        >
          <div className={styles.distLinkRow}>
            <Input
              readOnly
              value={
                dist.link_qr.fullUrl ||
                t("surveyManagement.distribute.link.regenerate_hint")
              }
            />
            <Button icon={<CopyOutlined />} onClick={copyLink}>
              {t("surveyManagement.distribute.link.copy")}
            </Button>
            {dist.link_qr.slug ? (
              // A new link retires the current one, its printed QR codes and the website widget.
              <Popconfirm
                title={t("surveyManagement.distribute.link.regenerate_confirm_title")}
                description={
                  <div className={styles.confirmText}>
                    {t("surveyManagement.distribute.link.regenerate_confirm_body")}
                  </div>
                }
                okText={t("surveyManagement.distribute.link.regenerate")}
                cancelText={t("surveyManagement.distribute.link.regenerate_cancel")}
                onConfirm={regenerate}
              >
                <Button type="primary" icon={<ReloadOutlined />}>
                  {t("surveyManagement.distribute.link.regenerate")}
                </Button>
              </Popconfirm>
            ) : (
              <Button type="primary" icon={<ReloadOutlined />} onClick={regenerate}>
                {t("surveyManagement.distribute.link.regenerate")}
              </Button>
            )}
          </div>
          <div className={styles.slugRow}>
            <LinkSlugEditor
              instrumentId={instrumentId}
              slug={dist.link_qr.slug}
              fullUrl={dist.link_qr.fullUrl}
              onSaved={({ slug, fullUrl }) => {
                setChannel("link_qr", { slug, fullUrl });
                if (onLinkRegenerated) onLinkRegenerated({ slug, fullUrl });
              }}
            />
          </div>
          <KioskModeToggle
            instrumentId={instrumentId}
            slug={dist.link_qr.slug}
            fullUrl={dist.link_qr.fullUrl}
            kioskKey={dist.link_qr.kioskKey}
            onChange={(kioskKey) => setChannel("link_qr", { kioskKey })}
          />

          <p className={styles.fieldHint}>{t("surveyManagement.distribute.link.closing_moved")}</p>
          <div className={styles.distGrid2}>
            <div className={styles.distField}>
              <label className={styles.fieldLabel}>
                {t("surveyManagement.distribute.link.language_label")}
              </label>
              <Select
                value={dist.link_qr.languageMode}
                onChange={(v) => setChannel("link_qr", { languageMode: v })}
                options={[
                  {
                    value: "auto",
                    label: t("surveyManagement.distribute.link.lang_auto"),
                  },
                  {
                    value: "fixed",
                    label: t("surveyManagement.distribute.link.lang_fixed"),
                  },
                ]}
              />
            </div>
            {dist.link_qr.languageMode === "fixed" ? (
              <div className={styles.distField}>
                <label className={styles.fieldLabel}>
                  {t(
                    "surveyManagement.distribute.link.fixed_language_label"
                  )}
                </label>
                <Select
                  value={dist.link_qr.fixedLanguage}
                  onChange={(v) =>
                    setChannel("link_qr", { fixedLanguage: v })
                  }
                  options={[
                    { value: "en", label: "English" },
                    { value: "tr", label: "Türkçe" },
                  ]}
                />
              </div>
            ) : null}
          </div>
        </Card>
      ) : null}

      {dist.web_embed.enabled ? (
        <Card
          size="small"
          className={styles.distPanel}
          title={
            <span>
              <CodeOutlined />{" "}
              {t("surveyManagement.distribute.embed.panel_title")}
            </span>
          }
        >
          <label className={styles.fieldLabel}>
            {t("surveyManagement.distribute.embed.snippet_label")}
          </label>
          <pre className={styles.distCodeBlock}>{embedSnippet}</pre>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={copySnippet}
            className={styles.distSnippetCopy}
          >
            {t("surveyManagement.distribute.embed.copy_snippet")}
          </Button>

          <div className={styles.distField}>
            <label className={styles.fieldLabel}>
              {t("surveyManagement.distribute.embed.display_mode_label")}
            </label>
            <Select
              value={dist.web_embed.displayMode}
              onChange={(v) => setChannel("web_embed", { displayMode: v })}
              options={[
                {
                  value: "popup",
                  label: t("surveyManagement.distribute.embed.popup"),
                },
                {
                  value: "fullscreen",
                  label: t("surveyManagement.distribute.embed.fullscreen"),
                },
                {
                  value: "inline",
                  label: t("surveyManagement.distribute.embed.inline"),
                },
              ]}
            />
          </div>

          <div className={styles.distField}>
            <label className={styles.fieldLabel}>
              {t("surveyManagement.distribute.embed.trigger_preset_label")}
            </label>
            <Segmented
              value={dist.web_embed.triggerPreset || EMBED_TRIGGER_PRESET.LAUNCHER_CLICK}
              onChange={setTriggerPreset}
              options={[
                {
                  label: t("surveyManagement.distribute.embed.preset_launcher"),
                  value: EMBED_TRIGGER_PRESET.LAUNCHER_CLICK,
                },
                {
                  label: t("surveyManagement.distribute.embed.preset_page_load"),
                  value: EMBED_TRIGGER_PRESET.PAGE_LOAD,
                },
                {
                  label: t("surveyManagement.distribute.embed.preset_custom"),
                  value: EMBED_TRIGGER_PRESET.CUSTOM,
                },
              ]}
            />
            <p className={styles.fieldHint}>
              {isLauncherPreset
                ? t("surveyManagement.distribute.embed.preset_launcher_hint")
                : isPageLoadPreset
                  ? t("surveyManagement.distribute.embed.preset_page_load_hint")
                  : t("surveyManagement.distribute.embed.preset_custom_hint")}
            </p>
          </div>

          {isLauncherPreset ? (
            <>
              <div className={styles.distField}>
                <label className={styles.fieldLabel}>
                  {t("surveyManagement.distribute.embed.launcher_label")}
                </label>
                <Input
                  value={dist.web_embed.launcherLabel || ""}
                  placeholder={t(
                    "surveyManagement.distribute.embed.launcher_label_placeholder"
                  )}
                  maxLength={48}
                  onChange={(e) =>
                    setChannel("web_embed", { launcherLabel: e.target.value })
                  }
                />
                <p className={styles.fieldHint}>
                  {t("surveyManagement.distribute.embed.launcher_label_hint")}
                </p>
              </div>
              <div className={styles.distField}>
                <label className={styles.fieldLabel}>
                  {t("surveyManagement.distribute.embed.launcher_position_label")}
                </label>
                <Segmented
                  value={
                    dist.web_embed.launcherPosition ||
                    EMBED_LAUNCHER_POSITION.RIGHT
                  }
                  onChange={(v) =>
                    setChannel("web_embed", { launcherPosition: v })
                  }
                  options={[
                    {
                      label: t(
                        "surveyManagement.distribute.embed.launcher_position_right"
                      ),
                      value: EMBED_LAUNCHER_POSITION.RIGHT,
                    },
                    {
                      label: t(
                        "surveyManagement.distribute.embed.launcher_position_left"
                      ),
                      value: EMBED_LAUNCHER_POSITION.LEFT,
                    },
                  ]}
                />
              </div>
            </>
          ) : null}

          <div className={styles.distGrid2}>
            <div className={styles.distField}>
              <label className={styles.fieldLabel}>
                {t("surveyManagement.distribute.embed.device_target_label")}
              </label>
              <Select
                value={dist.web_embed.deviceTarget || EMBED_DEVICE_TARGET.ALL}
                onChange={(v) => setChannel("web_embed", { deviceTarget: v })}
                options={[
                  {
                    value: EMBED_DEVICE_TARGET.ALL,
                    label: t("surveyManagement.distribute.embed.device_all"),
                  },
                  {
                    value: EMBED_DEVICE_TARGET.MOBILE,
                    label: t("surveyManagement.distribute.embed.device_mobile"),
                  },
                  {
                    value: EMBED_DEVICE_TARGET.DESKTOP,
                    label: t("surveyManagement.distribute.embed.device_desktop"),
                  },
                ]}
              />
            </div>
            <div className={styles.distField}>
              <div className={styles.settingRow}>
                <div className={styles.settingRowText}>
                  <span className={styles.fieldLabel}>
                    {t("surveyManagement.distribute.embed.once_per_day_label")}
                  </span>
                  <span className={styles.fieldHint}>
                    {t("surveyManagement.distribute.embed.once_per_day_hint")}
                  </span>
                </div>
                <Switch
                  checked={!!dist.web_embed.oncePerDay}
                  onChange={(checked) =>
                    setChannel("web_embed", { oncePerDay: checked })
                  }
                />
              </div>
            </div>
          </div>

          {showCustomTriggerRules ? (
            <>
          <div className={styles.distTriggerHeader}>
            <label className={styles.fieldLabel}>
              {t("surveyManagement.distribute.embed.triggers_label")}
            </label>
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addRule}
            >
              {t("surveyManagement.distribute.embed.add_rule")}
            </Button>
          </div>
          <div className={styles.distRuleList}>
            {dist.web_embed.triggerRules.map((rule, idx) => (
              <div className={styles.distRuleRow} key={idx}>
                <Select
                  size="small"
                  className={styles.distRuleType}
                  value={rule.rule_type}
                  onChange={(v) => updateRule(idx, { rule_type: v })}
                  options={EMBED_TRIGGER_TYPES.map((rt) => ({
                    value: rt,
                    label: t(`surveyManagement.distribute.embed.rule_${rt}`),
                  }))}
                />
                {renderRuleValue(rule, idx)}
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeRule(idx)}
                  aria-label={t("surveyManagement.distribute.embed.remove_rule")}
                />
              </div>
            ))}
          </div>
            </>
          ) : null}

          <div className={styles.distField}>
            <label className={styles.fieldLabel}>
              {t("surveyManagement.distribute.embed.url_match_label")}
            </label>
            <Input
              value={dist.web_embed.urlMatch}
              placeholder={t(
                "surveyManagement.distribute.embed.url_match_placeholder"
              )}
              onChange={(e) =>
                setChannel("web_embed", { urlMatch: e.target.value })
              }
            />
            <p className={styles.fieldHint}>
              {t("surveyManagement.distribute.embed.url_match_hint")}
            </p>
          </div>
        </Card>
      ) : null}

      {dist.phone_call?.enabled ? (
        <Card
          size="small"
          className={styles.distPanel}
          title={
            <span>
              <PhoneOutlined /> {t("surveyManagement.distribute.phone.panel_title")}
            </span>
          }
        >
          <div className={styles.distGrid2}>
            <div className={styles.distField}>
              <label className={styles.fieldLabel}>{t("surveyManagement.distribute.phone.language")}</label>
              <Select
                value={callLanguage}
                onChange={(v) => setChannel("phone_call", { language: v })}
                options={CALL_LANGUAGES}
              />
            </div>
            <div className={styles.distField}>
              <label className={styles.fieldLabel}>{t("surveyManagement.distribute.phone.persona")}</label>
              <Select
                value={dist.phone_call.persona}
                onChange={(v) => setChannel("phone_call", { persona: v })}
                options={PERSONA_OPTIONS.map((p) => ({ value: p.value, label: t(p.labelKey) }))}
              />
            </div>
          </div>
          <div className={styles.distField}>
            <label className={styles.fieldLabel}>{t("surveyManagement.distribute.phone.greeting")}</label>
            <Input.TextArea
              rows={2}
              maxLength={400}
              value={dist.phone_call.greeting}
              placeholder={t("surveyManagement.distribute.phone.greeting_ph")}
              onChange={(e) => setChannel("phone_call", { greeting: e.target.value })}
            />
            <p className={styles.fieldHint}>{t("surveyManagement.distribute.phone.greeting_hint")}</p>
          </div>
          <div className={styles.distField}>
            <label className={styles.fieldLabel}>{t("surveyManagement.distribute.phone.context")}</label>
            <Input.TextArea
              rows={2}
              maxLength={1000}
              value={dist.phone_call.context}
              placeholder={t("surveyManagement.distribute.phone.context_ph")}
              onChange={(e) => setChannel("phone_call", { context: e.target.value })}
            />
          </div>
          <div className={styles.distField}>
            <label className={styles.fieldLabel}>{t("surveyManagement.distribute.phone.disclosure")}</label>
            <Input.TextArea
              rows={2}
              maxLength={600}
              value={dist.phone_call.disclosure}
              placeholder={companyDisclosure || t("surveyManagement.distribute.phone.disclosure_ph")}
              onChange={(e) => setChannel("phone_call", { disclosure: e.target.value })}
            />
            <p className={styles.fieldHint}>
              {t(
                companyDisclosure
                  ? "surveyManagement.distribute.phone.disclosure_hint_company"
                  : "surveyManagement.distribute.phone.disclosure_hint"
              )}
            </p>
          </div>
          <div className={styles.distField}>
            <label className={styles.fieldLabel}>{t("surveyManagement.distribute.phone.knowledge_base")}</label>
            <SurveyKnowledgePanel
              instrumentId={instrumentId}
              questions={questions}
              locked={PHONE_CALLS_LOCKED}
              onLoaded={onKnowledgeLoaded}
            />
          </div>
          {/* The survey's old free-text ready answers stay editable until they are moved into
              the knowledge base; new surveys keep their answers there. */}
          {(dist.phone_call.knowledge || "").trim() ? (
            <div className={styles.distField}>
              <label className={styles.fieldLabel}>{t("surveyManagement.distribute.phone.knowledge_legacy")}</label>
              <Input.TextArea
                rows={6}
                maxLength={4000}
                showCount
                value={dist.phone_call.knowledge}
                placeholder={t("surveyManagement.distribute.phone.knowledge_ph")}
                onChange={(e) => setChannel("phone_call", { knowledge: e.target.value })}
              />
              <p className={styles.fieldHint}>{t("surveyManagement.distribute.phone.knowledge_legacy_hint")}</p>
            </div>
          ) : null}
          <PhoneVoiceTest
            instrumentId={instrumentId}
            surveyTitle={surveyTitle}
            questions={questions}
            phoneCall={dist.phone_call}
            language={callLanguage}
            onChange={(patch) => setChannel("phone_call", patch)}
          />
          <PhoneTriggerPanel
            instrumentId={instrumentId}
            phoneCall={dist.phone_call}
            channelSaved={!!dist.phone_call.exists}
            locked={PHONE_CALLS_LOCKED}
            onChange={(patch) => setChannel("phone_call", patch)}
          />
        </Card>
      ) : null}

      {dist.triggered_send.enabled ? (
        <Card
          size="small"
          className={styles.distPanel}
          title={
            <span>
              <ThunderboltOutlined />{" "}
              {t("surveyManagement.distribute.triggered.panel_title")}
            </span>
          }
        >
          <div className={styles.distGrid2}>
            <div className={styles.distField}>
              <label className={styles.fieldLabel}>
                {t("surveyManagement.distribute.triggered.source_label")}
              </label>
              <Select
                value={dist.triggered_send.eventSource}
                onChange={(v) => {
                  if (
                    !canAdvancedTriggers &&
                    ["zendesk", "salesforce"].includes(v)
                  ) {
                    message.info(
                      t("surveyManagement.distribute.triggered.advanced_locked_toast"),
                    );
                    if (onUpgrade) onUpgrade("pro_plus");
                    return;
                  }
                  setChannel("triggered_send", { eventSource: v });
                }}
                options={[
                  { value: "sonic", label: "Pivony Sonic" },
                  { value: "shopify", label: "Shopify" },
                  {
                    value: "zendesk",
                    label: canAdvancedTriggers
                      ? "Zendesk"
                      : "Zendesk (Pro+)",
                    disabled: !canAdvancedTriggers && !allowedSources.includes("zendesk"),
                  },
                  {
                    value: "salesforce",
                    label: canAdvancedTriggers
                      ? "Salesforce"
                      : "Salesforce (Pro+)",
                    disabled:
                      !canAdvancedTriggers &&
                      !allowedSources.includes("salesforce"),
                  },
                ]}
              />
            </div>
            <div className={styles.distField}>
              <label className={styles.fieldLabel}>
                {t("surveyManagement.distribute.triggered.event_label")}
              </label>
              <Input
                value={dist.triggered_send.eventName}
                placeholder="order.delivered"
                onChange={(e) =>
                  setChannel("triggered_send", { eventName: e.target.value })
                }
              />
            </div>
            <div className={styles.distField}>
              <label className={styles.fieldLabel}>
                {t("surveyManagement.distribute.triggered.delay_label")}
              </label>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                value={dist.triggered_send.delayMinutes}
                onChange={(v) =>
                  setChannel("triggered_send", { delayMinutes: v })
                }
              />
            </div>
            <div className={styles.distField}>
              <label className={styles.fieldLabel}>
                {t("surveyManagement.distribute.triggered.send_via_label")}
              </label>
              <Select
                value={dist.triggered_send.channelForSend}
                onChange={(v) =>
                  setChannel("triggered_send", { channelForSend: v })
                }
                options={[
                  { value: "email", label: "Email" },
                  { value: "sms", label: "SMS" },
                  { value: "whatsapp", label: "WhatsApp" },
                ]}
              />
            </div>
          </div>
          <div className={styles.distFreqRow}>
            <span>{t("surveyManagement.distribute.triggered.freq_prefix")}</span>
            <InputNumber
              size="small"
              min={1}
              className={styles.distFreqNum}
              value={dist.triggered_send.freqMax}
              onChange={(v) => setChannel("triggered_send", { freqMax: v })}
            />
            <span>{t("surveyManagement.distribute.triggered.freq_mid")}</span>
            <InputNumber
              size="small"
              min={1}
              className={styles.distFreqNum}
              value={dist.triggered_send.freqDays}
              onChange={(v) => setChannel("triggered_send", { freqDays: v })}
            />
            <span>{t("surveyManagement.distribute.triggered.freq_suffix")}</span>
          </div>
          {dist.triggered_send.channelForSend === "whatsapp" ? (
            <div className={styles.distNote}>
              {t("surveyManagement.distribute.triggered.whatsapp_note")}
            </div>
          ) : null}
        </Card>
      ) : null}

      {dist.in_app_sdk.enabled ? (
        <Card
          size="small"
          className={styles.distPanel}
          title={
            <span>
              <MobileOutlined />{" "}
              {t("surveyManagement.distribute.in_app.panel_title")}
            </span>
          }
        >
          <div className={styles.distComingSoon}>
            {t("surveyManagement.distribute.in_app.coming_soon")}
          </div>
        </Card>
      ) : null}
    </div>
  );
};

export default DistributeStep;
